import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { 
  ArrowLeft, Check, Star, Zap, Shield, Crown,
  CreditCard, Loader2, Heart, Clock, Users, FileText, Globe, ChevronDown, CheckCircle
} from 'lucide-react';
import { detectCurrency, getLocalizedPrice, PRICING, getCurrencyInfo, getCurrencyForLanguage, getLocalizedPriceForPlan } from '../utils/currency';
import { useLanguage } from '../contexts/LanguageContext';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HealthAppPricing = () => {
  const navigate = useNavigate();
  const { t, language, setLanguage, supportedLanguages } = useLanguage();
  const [selectedPlan, setSelectedPlan] = useState('annual');
  const [loading, setLoading] = useState(false);
  const [currency, setCurrency] = useState('USD');
  const [prices, setPrices] = useState({});

  useEffect(() => {
    // Get currency based on selected language
    const languageCurrency = getCurrencyForLanguage(language);
    setCurrency(languageCurrency);
    
    // Get localized prices (uses region-specific pricing, not just conversion)
    const localPrices = {
      monthly: getLocalizedPriceForPlan('monthly', languageCurrency),
      annual: getLocalizedPriceForPlan('annual', languageCurrency),
      lifetime: getLocalizedPriceForPlan('lifetime', languageCurrency),
    };
    setPrices(localPrices);
  }, [language]);  // Re-calculate when language changes

  const plans = [
    {
      id: 'monthly',
      name: language === 'hi' ? 'मासिक' : 'Monthly',
      icon: Clock,
      color: 'blue',
      features: language === 'hi' ? [
        'संपूर्ण चिकित्सा इतिहास',
        'असीमित दवाइयाँ और सप्लीमेंट',
        'स्वास्थ्य डायरी और रिमाइंडर',
        'आपातकालीन प्रोफ़ाइल और QR कोड',
        'PDF निर्यात और साझाकरण',
      ] : [
        'Full medical history tracking',
        'Unlimited prescriptions & supplements',
        'Health diary & reminders',
        'Emergency profile & QR code',
        'PDF export & sharing',
      ],
    },
    {
      id: 'annual',
      name: language === 'hi' ? 'वार्षिक' : 'Annual',
      icon: Star,
      color: 'green',
      badge: language === 'hi' ? '64% बचाएं' : 'Save 64%',
      popular: true,
      features: language === 'hi' ? [
        'मासिक की सभी सुविधाएं',
        '🤖 आयु AI स्वास्थ्य सहायक',
        'प्राथमिकता सहायता',
        'उन्नत स्वास्थ्य विश्लेषण',
        'परिवार के सदस्यों की प्रोफ़ाइल',
      ] : [
        'Everything in Monthly',
        '🤖 Aayu AI Health Assistant',
        'Priority support',
        'Advanced health insights',
        'Family member profiles',
      ],
    },
    {
      id: 'lifetime',
      name: language === 'hi' ? 'आजीवन' : 'Lifetime',
      icon: Crown,
      color: 'purple',
      badge: language === 'hi' ? 'सर्वोत्तम मूल्य' : 'Best Value',
      features: language === 'hi' ? [
        'वार्षिक की सभी सुविधाएं',
        '🤖 आयु AI स्वास्थ्य सहायक',
        'एक बार भुगतान, हमेशा के लिए',
        'सभी भविष्य की सुविधाएं शामिल',
        'संस्थापक सदस्य का दर्जा',
      ] : [
        'Everything in Annual',
        '🤖 Aayu AI Health Assistant',
        'One-time payment, forever access',
        'All future features included',
        'Founding member status',
      ],
    },
  ];

  const handleSubscribe = async () => {
    setLoading(true);
    
    try {
      const response = await axios.post(`${API_URL}/payments/checkout/health-subscription`, {
        plan: selectedPlan,
        origin_url: window.location.origin,
        currency: currency,
      });
      
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      console.error('Payment error:', error);
      toast.error('Failed to start checkout. Please try again.');
      setLoading(false);
    }
  };

  // EC Health Brand Colors - Consistent Teal Theme
  const BRAND = {
    teal: '#0d9488',      // Primary teal-600
    tealDark: '#0f766e',  // teal-700 for hover
    tealLight: '#14b8a6', // teal-500
    safeGreen: '#22c55e', // Green for checkmarks
  };

  const getColorClasses = (color, isSelected) => {
    // Consistent teal styling
    return {
      bg: isSelected ? 'bg-teal-50 border-teal-500' : 'bg-white border-gray-200',
      icon: 'bg-teal-100 text-teal-600',
      button: 'bg-teal-600 hover:bg-teal-700',
    };
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-teal-50 to-white">
      {/* Header - Teal Theme */}
      <header className="bg-teal-600 text-white px-4 py-4 sticky top-0 z-40">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="text-left">
            <h1 className="text-xl font-bold">EmergencyCard Health</h1>
            <p className="text-xs text-white/70">
              {language === 'hi' ? 'आपका संपूर्ण स्वास्थ्य पासपोर्ट' : 'Your Complete Health Passport'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {/* Language Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="text-white hover:bg-white/10">
                  <Globe className="h-4 w-4 mr-1" />
                  {supportedLanguages?.find(l => l.code === language)?.flag}
                  <ChevronDown className="h-3 w-3 ml-1" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                {supportedLanguages?.map((lang) => (
                  <DropdownMenuItem
                    key={lang.code}
                    onClick={() => setLanguage(lang.code)}
                    className={`cursor-pointer ${language === lang.code ? 'bg-teal-50 font-semibold' : ''}`}
                  >
                    <span className="mr-2">{lang.flag}</span>
                    {lang.name}
                    {language === lang.code && <CheckCircle className="h-4 w-4 ml-auto text-teal-500" />}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            <Button
              variant="outline"
              onClick={() => navigate('/login?existing=true')}
              className="bg-transparent border-white/30 text-white hover:bg-white/10 hover:text-white"
              data-testid="login-btn"
            >
              {language === 'hi' ? 'लॉगिन' : 'Login'}
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto p-4 pb-12">
        {/* Hero */}
        <div className="text-center py-8">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-800 mb-3">
            {language === 'hi' ? 'आपका संपूर्ण स्वास्थ्य पासपोर्ट' : 'Your Complete Health Passport'}
          </h2>
          <p className="text-slate-600 max-w-lg mx-auto">
            {language === 'hi' 
              ? 'अपना संपूर्ण चिकित्सा इतिहास सुरक्षित रखें। कहीं भी एक्सेस करें, डॉक्टर को तुरंत दिखाएं।'
              : 'Store your entire medical history securely. Access it anywhere, share with doctors instantly.'}
          </p>
          
          {/* Currency indicator */}
          {currency !== 'USD' && (
            <p className="text-sm text-slate-500 mt-2">
              Prices shown in {getCurrencyInfo(currency).name} ({currency})
            </p>
          )}
        </div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-3 gap-4 mb-8 items-stretch">
          {plans.map((plan) => {
            const isSelected = selectedPlan === plan.id;
            const colors = getColorClasses(plan.color, isSelected);
            const price = prices[plan.id];
            
            return (
              <Card 
                key={plan.id}
                className={`relative cursor-pointer transition-all ${colors.bg} border-2 ${isSelected ? 'shadow-lg scale-[1.02]' : 'hover:border-slate-300'} flex flex-col h-full`}
                onClick={() => setSelectedPlan(plan.id)}
                data-testid={`plan-${plan.id}`}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <Badge className="bg-teal-600 text-white">Most Popular</Badge>
                  </div>
                )}
                {plan.badge && !plan.popular && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <Badge className="bg-teal-600 text-white">{plan.badge}</Badge>
                  </div>
                )}
                
                <CardHeader className="pb-2 pt-6">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${colors.icon}`}>
                      <plan.icon className="h-5 w-5" />
                    </div>
                    <CardTitle className="text-lg">{plan.name}</CardTitle>
                  </div>
                </CardHeader>
                
                <CardContent className="flex-1 flex flex-col">
                  {/* Price */}
                  <div className="mb-4">
                    <div className="flex items-baseline gap-1">
                      <span className="text-3xl font-bold text-slate-800">
                        {price?.formatted || '...'}
                      </span>
                      <span className="text-slate-500 text-sm">
                        {plan.id === 'monthly' && '/month'}
                        {plan.id === 'annual' && '/year'}
                        {plan.id === 'lifetime' && 'once'}
                      </span>
                    </div>
                    {plan.id === 'annual' && (
                      <p className="text-teal-600 text-sm font-medium">
                        {language === 'hi' ? 'मासिक से 64% बचाएं' : 'Save 64% vs monthly'}
                      </p>
                    )}
                    {plan.id !== 'annual' && (
                      <p className="text-transparent text-sm font-medium select-none">
                        &nbsp;
                      </p>
                    )}
                    {currency !== 'USD' && price && (
                      <p className="text-slate-400 text-xs">
                        ~{price.usdFormatted} USD
                      </p>
                    )}
                  </div>
                  
                  {/* Features */}
                  <ul className="space-y-2 flex-1">
                    {plan.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm">
                        <Check className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                        <span className="text-slate-600">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  
                  {/* Selection indicator */}
                  <div className={`mt-4 flex items-center justify-center gap-2 text-sm font-medium h-6 ${isSelected ? 'text-teal-600' : 'text-transparent'}`}>
                    <Check className="h-4 w-4" />
                    Selected
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Subscribe Button */}
        <div className="max-w-md mx-auto">
          <Button 
            onClick={handleSubscribe}
            disabled={loading}
            className={`w-full h-12 text-lg ${getColorClasses(plans.find(p => p.id === selectedPlan)?.color, true).button}`}
            data-testid="subscribe-btn"
          >
            {loading ? (
              <>
                <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                {language === 'hi' ? 'चेकआउट पर जा रहे हैं...' : 'Redirecting to checkout...'}
              </>
            ) : (
              <>
                <CreditCard className="h-5 w-5 mr-2" />
                {language === 'hi' 
                  ? `${plans.find(p => p.id === selectedPlan)?.name} सदस्यता लें`
                  : `Subscribe to ${plans.find(p => p.id === selectedPlan)?.name}`}
              </>
            )}
          </Button>
          
          <div className="flex items-center justify-center gap-4 mt-4 text-xs text-slate-500">
            <span className="flex items-center gap-1">
              <Shield className="h-3 w-3" />
              {language === 'hi' ? 'सुरक्षित भुगतान' : 'Secure checkout'}
            </span>
            <span>•</span>
            <span>{language === 'hi' ? 'कभी भी रद्द करें' : 'Cancel anytime'}</span>
            <span>•</span>
            <span>Powered by Stripe</span>
          </div>
        </div>

        {/* Features Overview */}
        <div className="mt-12 grid md:grid-cols-3 gap-6">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-teal-100 mb-3">
              <FileText className="h-6 w-6 text-teal-600" />
            </div>
            <h3 className="font-bold text-slate-800 mb-1">
              {language === 'hi' ? 'पूर्ण रिकॉर्ड' : 'Complete Records'}
            </h3>
            <p className="text-sm text-slate-600">
              {language === 'hi' 
                ? 'दवाइयाँ, लैब रिपोर्ट, चिकित्सा इतिहास और बहुत कुछ सहेजें।'
                : 'Store prescriptions, lab reports, medical history, and more.'}
            </p>
          </div>
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-teal-100 mb-3">
              <Users className="h-6 w-6 text-teal-600" />
            </div>
            <h3 className="font-bold text-slate-800 mb-1">
              {language === 'hi' ? 'डॉक्टर से साझा करें' : 'Doctor Sharing'}
            </h3>
            <p className="text-sm text-slate-600">
              {language === 'hi' 
                ? 'सुरक्षित QR कोड से किसी भी डॉक्टर को रिकॉर्ड दिखाएं।'
                : 'Share your records with any doctor via secure QR code.'}
            </p>
          </div>
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-teal-100 mb-3">
              <Heart className="h-6 w-6 text-teal-600" />
            </div>
            <h3 className="font-bold text-slate-800 mb-1">
              {language === 'hi' ? 'स्वास्थ्य विश्लेषण' : 'Health Insights'}
            </h3>
            <p className="text-sm text-slate-600">
              {language === 'hi' 
                ? 'ट्रेंड ट्रैक करें और व्यक्तिगत स्वास्थ्य सुझाव पाएं।'
                : 'Track trends and get personalized health recommendations.'}
            </p>
          </div>
        </div>

        {/* Footer Links */}
        <div className="mt-8 pt-6 border-t border-slate-200 text-center text-sm text-slate-500">
          <div className="flex items-center justify-center gap-4 flex-wrap">
            <a href="/privacy-policy" className="hover:text-teal-600 transition-colors">
              {language === 'hi' ? 'गोपनीयता नीति' : 'Privacy Policy'}
            </a>
            <span>•</span>
            <a href="/terms" className="hover:text-teal-600 transition-colors">
              {language === 'hi' ? 'सेवा की शर्तें' : 'Terms of Service'}
            </a>
            <span>•</span>
            <span>© 2026 EmergencyCard Health</span>
          </div>
        </div>
      </main>
    </div>
  );
};

export default HealthAppPricing;
